/**
 * 통합서버 map_data(맵 에디터 내보내기의 mapData) → 그릴 도형.
 * offline-manage 콘솔 맵(map.js buildFloor / lightsmon.js floorAssets)과 같은 키를 같은 방식으로 읽는다.
 * 좌표는 맵 좌표 그대로(y 가 아래로 커짐 — 화면과 같아 뒤집지 않는다).
 */
export type Pt = [number, number];

export interface QaPolygon {
  id: string;
  name: string;
  pts: Pt[];
  /** 조명이면 연결 CCTV 이름 (linkedCctvId). */
  cctv?: string;
}
export interface QaCamera {
  id: string;
  name: string;
  ip: string;
  x: number;
  y: number;
  kind: 'light' | 'warning';
  linked: string[];
}
export interface QaLabel {
  text: string;
  x: number;
  y: number;
}
/** 비상벨 — 위치와 이름·벨 ID(맵 linkedCustomId). 점검 탭에서 문제 벨을 찾는 키. */
export interface QaBell {
  x: number;
  y: number;
  name: string;
  customId: string;
}

export interface QaFloorShapes {
  lines: Pt[][];
  zones: QaPolygon[];
  columns: QaPolygon[];
  parking: QaPolygon[];
  lights: QaPolygon[];
  bells: QaBell[];
  cameras: QaCamera[];
  labels: QaLabel[];
  bounds: { minX: number; minY: number; maxX: number; maxY: number } | null;
}

type Obj = Record<string, unknown>;

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** [x,y] 와 {x,y} 둘 다 받는다 (offline-manage mapdata point 와 같다). */
function pt(v: unknown): Pt | null {
  if (Array.isArray(v)) {
    const x = num(v[0]);
    const y = num(v[1]);
    return x !== null && y !== null ? [x, y] : null;
  }
  if (v && typeof v === 'object') {
    const x = num((v as Obj).x);
    const y = num((v as Obj).y);
    return x !== null && y !== null ? [x, y] : null;
  }
  return null;
}

const pts = (v: unknown): Pt[] => (Array.isArray(v) ? v.map(pt).filter((p): p is Pt => !!p) : []);
const list = (v: unknown): Obj[] =>
  Array.isArray(v) ? v.filter((o): o is Obj => !!o && typeof o === 'object') : [];
const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

function centroid(p: Pt[]): Pt {
  const n = p.length || 1;
  return [p.reduce((s, q) => s + q[0], 0) / n, p.reduce((s, q) => s + q[1], 0) / n];
}

/** 객체 하나의 위치 — points 가 있으면 무게중심, 없으면 position (+size/2 는 하지 않는다: position 이 이미 중심). */
function anchor(o: Obj): Pt | null {
  const p = pts(o.points);
  if (p.length) return centroid(p);
  return pt(o.position);
}

/** 폴리곤 — points 가 있으면 그대로, 없으면 position+width/height 사각형. 상대좌표(guideBoards 처럼 position 기준)는 다루지 않는다. */
function polygonOf(o: Obj): Pt[] {
  const p = pts(o.points);
  if (p.length >= 3) return p;
  const c = pt(o.position);
  const w = num(o.width);
  const h = num(o.height);
  if (!c || !w || !h) return [];
  return [
    [c[0] - w / 2, c[1] - h / 2],
    [c[0] + w / 2, c[1] - h / 2],
    [c[0] + w / 2, c[1] + h / 2],
    [c[0] - w / 2, c[1] + h / 2],
  ];
}

const poly = (o: Obj, i: number, prefix: string): QaPolygon => ({
  id: str(o.id) || `${prefix}-${i}`,
  name: str(o.name),
  pts: polygonOf(o),
  ...(str(o.linkedCctvId) ? { cctv: str(o.linkedCctvId) } : {}),
});

export function buildFloorShapes(mapData: unknown): QaFloorShapes {
  const md = (mapData && typeof mapData === 'object' ? mapData : {}) as Obj;

  const lines = ['outLines', 'innerLines', 'arrows']
    .flatMap((k) => list(md[k]).map((o) => pts(o.points)))
    .filter((p) => p.length >= 2);
  const zones = list(md.zones)
    .map((o, i) => poly(o, i, 'zone'))
    .filter((p) => p.pts.length >= 3);
  const columns = [...list(md.columns), ...list(md.elevators)]
    .map((o, i) => poly(o, i, 'col'))
    .filter((p) => p.pts.length >= 3);
  const parking = list(md.parkingLocations)
    .map((o, i) => poly(o, i, 'park'))
    .filter((p) => p.pts.length >= 3);
  const lights = list(md.lights)
    .map((o, i) => poly(o, i, 'light'))
    .filter((p) => p.pts.length >= 3);
  const bells: QaBell[] = list(md.emergencyBells).flatMap((o) => {
    const p = anchor(o);
    return p ? [{ x: p[0], y: p[1], name: str(o.name), customId: str(o.linkedCustomId) }] : [];
  });

  const cctvs = (md.cctvs && typeof md.cctvs === 'object' ? md.cctvs : {}) as Obj;
  const cameras: QaCamera[] = (
    [
      ['light', list(cctvs.lightCctvs)],
      ['warning', list(cctvs.warningCctvs)],
    ] as const
  ).flatMap(([kind, arr]) =>
    arr.flatMap((o, i) => {
      const a = anchor(o);
      if (!a) return [];
      return [
        {
          id: str(o.id) || `${kind}-${i}`,
          name: str(o.name),
          ip: str(o.ipAddress),
          x: a[0],
          y: a[1],
          kind,
          linked: Array.isArray(o.linkedParkingLocationIds)
            ? o.linkedParkingLocationIds.map(str)
            : [],
        },
      ];
    }),
  );

  const labels: QaLabel[] = list(md.zones).flatMap((o) => {
    const text = str(o.name) || str(o.zoneName);
    const a = anchor(o);
    return text && a ? [{ text, x: a[0], y: a[1] }] : [];
  });

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const grow = (p: Pt) => {
    minX = Math.min(minX, p[0]);
    minY = Math.min(minY, p[1]);
    maxX = Math.max(maxX, p[0]);
    maxY = Math.max(maxY, p[1]);
  };
  lines.forEach((l) => l.forEach(grow));
  [zones, columns, parking, lights].forEach((g) => g.forEach((p) => p.pts.forEach(grow)));
  bells.forEach((b) => grow([b.x, b.y]));
  cameras.forEach((c) => grow([c.x, c.y]));

  return {
    lines,
    zones,
    columns,
    parking,
    lights,
    bells,
    cameras,
    labels,
    bounds: Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null,
  };
}
